<template>
    <themed-modal
        name="tour-comps-modal"
        title="Manage Tour Comps"
        size="lg"
        :show-footer="false"
        @closed="closeModal"
    >
        <div class="tour-comps-modal">
            <!-- Current Comps Section -->
            <div class="tour-comps-modal__section">
                <h4 class="tour-comps-modal__section-title">
                    Current Comps ({{ tourComps.length }})
                </h4>
                
                <div v-if="tourComps.length === 0" class="tour-comps-modal__empty">
                    No comps added yet. Select comps below to add them to this tour.
                </div>

                <draggable
                    v-else
                    v-model="tourComps"
                    @end="reorderComps"
                    class="tour-comps-modal__list"
                    handle=".tour-comps-modal__drag-handle"
                    :animation="200"
                >
                    <div
                        v-for="tourComp in tourComps"
                        :key="tourComp.id"
                        class="tour-comps-modal__item"
                    >
                        <div class="tour-comps-modal__drag-handle">
                            <theme-icon icon="arrows-vertical" type="icons" />
                        </div>
                        <div class="tour-comps-modal__item-content">
                            <span class="tour-comps-modal__item-name">
                                {{ tourComp.building?.building_name || tourComp.building?.address_line_1 || tourComp.comp?.building?.building_name || tourComp.comp?.building?.address_line_1 || 'Unnamed Comp' }}
                            </span>
                        </div>
                        <themed-button
                            @click="removeComp(tourComp)"
                            size="sm"
                            variant="destructive"
                        >
                            Remove
                        </themed-button>
                    </div>
                </draggable>
            </div>

            <!-- Available Comps Section -->
            <div class="tour-comps-modal__section">
                <h4 class="tour-comps-modal__section-title">Available Comps</h4>
                
                <div v-if="availableCompsToAdd.length === 0" class="tour-comps-modal__empty">
                    All available comps have been added to this tour.
                </div>

                <div v-else class="tour-comps-modal__available-list">
                    <div
                        v-for="comp in availableCompsToAdd"
                        :key="comp.pivot.id"
                        class="tour-comps-modal__available-item"
                    >
                        <span class="tour-comps-modal__available-name">
                            {{ comp.building_name || comp.address_line_1 || 'Unnamed Comp' }}
                        </span>
                        <themed-button
                            @click="addComp(comp)"
                            size="sm"
                            variant="default"
                        >
                            Add
                        </themed-button>
                    </div>
                </div>
            </div>
        </div>
    </themed-modal>
</template>

<script>
import axios from 'axios';
import draggable from 'vuedraggable';
import ThemedModal from '../ThemedModal.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemeIcon from '../ThemeIcon.vue';

export default {
    name: 'TourCompsModal',

    components: {
        draggable,
        ThemedModal,
        ThemedButton,
        ThemeIcon,
    },

    props: {
        tour: {
            type: Object,
            default: null,
        },
        availableComps: {
            type: Array,
            default: () => [],
        },
    },

    data() {
        return {
            tourComps: [],
            loading: false,
        };
    },

    computed: {
        tourCompIds() {
            return this.tourComps.map(tc => tc.comp_id);
        },
        availableCompsToAdd() {
            // Filter out comps that are already added to the tour
            // availableComps are Building objects with pivot data
            return this.availableComps.filter(comp => {
                const pivotId = comp.pivot?.id;
                return pivotId && !this.tourCompIds.includes(pivotId);
            });
        },
    },

    mounted() {
        this.$modal.show('tour-comps-modal');
        console.log('TourCompsModal mounted');
        console.log('Tour:', this.tour);
        console.log('Available Comps:', this.availableComps);
        if (this.tour) {
            this.loadTourComps();
        }
    },

    methods: {
        closeModal() {
            this.$modal.hide('tour-comps-modal');
        },
        
        async loadTourComps() {
            if (!this.tour) return;
            
            this.loading = true;
            try {
                const response = await axios.get(`/tours/${this.tour.id}`);
                this.tourComps = response.data.tour_comps || [];
                console.log('Loaded tour comps:', this.tourComps);
            } catch (error) {
                console.error('Error loading tour comps:', error);
                if (this.$toast) {
                    this.$toast.error('Failed to load tour comps');
                }
            } finally {
                this.loading = false;
            }
        },

        async addComp(comp) {
            // comp is a Building object with pivot data
            // pivot.id is the ProjectComp ID we need to send
            const compId = comp.pivot?.id;
            
            console.log('Adding comp:', comp);
            console.log('Building name:', comp.name);
            console.log('Pivot ID (ProjectComp ID):', compId);
            console.log('Tour project_id:', this.tour.project_id);
            
            if (!compId) {
                console.error('No pivot ID found for comp');
                if (this.$toast) {
                    this.$toast.error('Invalid comp data');
                }
                return;
            }
            
            try {
                await axios.post(`/tours/${this.tour.id}/comps`, {
                    comp_ids: [compId],
                });
                if (this.$toast) {
                    this.$toast.success('Comp added successfully');
                }
                this.loadTourComps();
                this.$emit('updated');
            } catch (error) {
                console.error('Error adding comp:', error);
                console.error('Error response:', error.response?.data);
                if (this.$toast) {
                    this.$toast.error(error.response?.data?.message || 'Failed to add comp');
                }
            }
        },

        async removeComp(tourComp) {
            const result = await this.$swal({
                title: 'Remove Comp?',
                text: 'Are you sure you want to remove this comp from the tour?',
                type: 'warning',
                buttonsStyling: false,
                confirmButtonText: 'Yes - Remove',
                confirmButtonClass: 'btn btn-red',
                showCancelButton: true,
                cancelButtonClass: 'btn btn-secondary mr-4',
            });

            if (!result.value) {
                return;
            }

            try {
                await axios.delete(`/tours/${this.tour.id}/comps/${tourComp.comp_id}`);
                if (this.$toast) {
                    this.$toast.success('Comp removed successfully');
                }
                this.loadTourComps();
                this.$emit('updated');
            } catch (error) {
                console.error('Error removing comp:', error);
                if (this.$toast) {
                    this.$toast.error('Failed to remove comp');
                }
            }
        },

        async reorderComps() {
            try {
                const compIds = this.tourComps.map(tc => tc.comp_id);
                await axios.put(`/tours/${this.tour.id}/comps/reorder`, {
                    comp_ids: compIds,
                });
                if (this.$toast) {
                    this.$toast.success('Comps reordered successfully');
                }
                this.$emit('updated');
            } catch (error) {
                console.error('Error reordering comps:', error);
                if (this.$toast) {
                    this.$toast.error('Failed to reorder comps');
                }
            }
        },
    },
};
</script>
